import React, { useContext, useEffect, useRef, useState } from "react"
import { Dropdown, DropdownMenu, DropdownToggle } from "reactstrap"
import { ToastContainer, toast } from "react-toastify"
import PerfectScrollbar from "react-perfect-scrollbar"
import * as Icon from "react-feather"
import { useHistory } from "react-router-dom"
import { NotificacionesContext } from "../../../context/notificaciones"
import "./NavbarNotificaciones.css"

const fechaRelativa = (fecha) => {
  const segundos = Math.max(Math.floor((Date.now() - new Date(fecha).getTime()) / 1000), 0)
  if (segundos < 60) return 'hace un momento'
  const minutos = Math.floor(segundos / 60)
  if (minutos < 60) return `hace ${minutos} min`
  const horas = Math.floor(minutos / 60)
  if (horas < 24) return `hace ${horas} h`
  const dias = Math.floor(horas / 24)
  if (dias < 30) return `hace ${dias} ${dias === 1 ? 'día' : 'días'}`
  return new Date(fecha).toLocaleDateString('es-CO')
}

// Apariencia por tipo de notificación (ver modelo en docs/frontend/notificaciones-socket.md del backend)
const TIPOS = {
  orden_servicio_asignada: { etiqueta: 'Asignada', variante: 'asignada', icono: Icon.Clipboard },
  orden_servicio_completada: { etiqueta: 'Completada', variante: 'completada', icono: Icon.CheckCircle }
}
const TIPO_DEFECTO = { etiqueta: 'Aviso', variante: 'aviso', icono: Icon.AlertCircle }

// Contenedor propio para que los avisos no se mezclen con otros toasts de la app
const CONTENEDOR_AVISOS = 'notificaciones'

const AvisoNotificacion = ({ notificacion }) => {
  const tipo = TIPOS[notificacion.tipo] || TIPO_DEFECTO
  const Icono = tipo.icono
  return (
    <div className={`notif-toast-contenido notif-${tipo.variante}`}>
      <div className="notif-icono"><Icono size={18} /></div>
      <div className="notif-cuerpo">
        <div className="notif-meta">
          <span className="notif-chip">{tipo.etiqueta}</span>
          <span className="notif-fecha">Nueva notificación</span>
        </div>
        <div className="notif-item-titulo">{notificacion.titulo}</div>
        <div className="notif-mensaje">{notificacion.mensaje}</div>
        {notificacion.url && <div className="notif-accion">Ver orden de servicio</div>}
      </div>
    </div>
  )
}

const NavbarNotificaciones = () => {
  const { notificaciones, totalNoLeidas, conectado, ultimaNueva, marcarLeida, marcarTodasLeidas } = useContext(NotificacionesContext)
  const [abierto, setAbierto] = useState(false)
  const history = useHistory()
  const avisosMostrados = useRef(new Set())

  const abrirNotificacion = (notificacion) => {
    marcarLeida(notificacion)
    setAbierto(false)
    if (notificacion.url) history.push(notificacion.url)
  }

  // Aviso emergente cuando llega una notificación en tiempo real (además del contador de la campana)
  useEffect(() => {
    if (!ultimaNueva || avisosMostrados.current.has(ultimaNueva.id)) return
    avisosMostrados.current.add(ultimaNueva.id)
    toast(<AvisoNotificacion notificacion={ultimaNueva} />, {
      containerId: CONTENEDOR_AVISOS,
      toastId: `notif-${ultimaNueva.id}`,
      className: 'notif-toast',
      onClick: () => abrirNotificacion(ultimaNueva)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ultimaNueva])

  return (
    <>
    <ToastContainer
      enableMultiContainer
      containerId={CONTENEDOR_AVISOS}
      position="top-right"
      autoClose={8000}
      newestOnTop
      closeOnClick
      pauseOnHover
    />
    <Dropdown tag="li" className="dropdown-notification nav-item notif" isOpen={abierto} toggle={() => setAbierto(!abierto)}>
      <DropdownToggle tag="a" className="nav-link nav-link-label">
        <Icon.Bell size={21} />
        {totalNoLeidas > 0 && (
          <span className="notif-contador">{totalNoLeidas > 9 ? '9+' : totalNoLeidas}</span>
        )}
      </DropdownToggle>
      <DropdownMenu tag="div" right className="notif-menu">
        <div className="notif-header">
          <div>
            <div className="notif-pendientes">
              {totalNoLeidas > 0 ? `${totalNoLeidas} ${totalNoLeidas === 1 ? 'pendiente' : 'pendientes'}` : 'Al día'}
            </div>
            <h5 className="notif-titulo">Notificaciones</h5>
          </div>
          <span className={`notif-estado ${conectado ? 'is-activo' : ''}`}>
            <span className="notif-estado-punto" />
            {conectado ? 'Avisos activos' : 'Sin conexión'}
          </span>
        </div>

        <PerfectScrollbar className="notif-lista" options={{ wheelPropagation: false }}>
          {notificaciones.length === 0 ? (
            <div className="notif-vacio">
              <Icon.BellOff size={28} />
              <span>No tienes notificaciones</span>
            </div>
          ) : notificaciones.map(notificacion => {
            const tipo = TIPOS[notificacion.tipo] || TIPO_DEFECTO
            const Icono = tipo.icono
            return (
              <div
                key={notificacion.id}
                role="button"
                className={`notif-item notif-${tipo.variante} ${notificacion.leida ? '' : 'is-no-leida'}`}
                onClick={() => abrirNotificacion(notificacion)}
              >
                <div className="notif-icono"><Icono size={18} /></div>
                <div className="notif-cuerpo">
                  <div className="notif-meta">
                    <span className="notif-chip">{tipo.etiqueta}</span>
                    <span className="notif-fecha">
                      {fechaRelativa(notificacion.fecha_creacion)}
                      {!notificacion.leida && <span className="notif-punto" />}
                    </span>
                  </div>
                  <div className="notif-item-titulo">{notificacion.titulo}</div>
                  <div className="notif-mensaje">{notificacion.mensaje}</div>
                  {notificacion.url && <div className="notif-accion">Ver orden de servicio</div>}
                </div>
              </div>
            )
          })}
        </PerfectScrollbar>

        {totalNoLeidas > 0 && (
          <div className="notif-footer">
            <button type="button" className="notif-boton" onClick={marcarTodasLeidas}>
              Marcar todo como visto
            </button>
          </div>
        )}
      </DropdownMenu>
    </Dropdown>
    </>
  )
}

export default NavbarNotificaciones
